import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const mixedPrecisionComponent: ComponentDef = {
  id: 'mixed-precision',
  name: 'Mixed Precision',
  description: 'FP16/BF16/FP8 forward compute with FP32 master weights and loss scaling for stable training.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'fp16',
      options: [
        { label: 'FP16 + FP32 master', value: 'fp16' },
        { label: 'BF16 + FP32 master', value: 'bf16' },
        { label: 'FP8 (E4M3, H100)', value: 'fp8' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const variant = String(params.variant);

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    if (variant === 'fp8') {
      // FP8 E4M3 path: quantize to FP8, compute in FP8, cast back through FP16 to FP32
      g.cast('x_fp8', 'x', [seq_len, d_model], 'FP8-E4M3');
      g.linear('w_master', 'x', [d_model, d_model]);
      g.cast('w_fp8', 'w_master', [d_model, d_model], 'FP8-E4M3');
      g.matmul('y_fp8', 'x_fp8', 'w_fp8', [seq_len, d_model]);
      g.activation('act_fp8', 'y_fp8', 'SiLU');
      // Dequantize FP8 -> FP16 intermediate for numerical stability
      g.cast('intermediate_fp16', 'act_fp8', [seq_len, d_model], 'FP16');
      g.scale('loss_scale', 'intermediate_fp16', 'Loss Scale');
      g.cast('out_fp32', 'loss_scale', [seq_len, d_model], 'FP32');
    } else if (variant === 'bf16') {
      // BF16 path: same structure as FP16 but with BF16 dtype
      g.cast('x_half', 'x', [seq_len, d_model], 'BF16');
      g.linear('w_master', 'x', [d_model, d_model]);
      g.cast('w_half', 'w_master', [d_model, d_model], 'BF16');
      g.matmul('y_half', 'x_half', 'w_half', [seq_len, d_model]);
      g.activation('act_half', 'y_half', 'SiLU');
      g.scale('loss_scale', 'act_half', 'Loss Scale');
      g.cast('out_fp32', 'loss_scale', [seq_len, d_model], 'FP32');
    } else {
      // FP16 path (default)
      g.cast('x_half', 'x', [seq_len, d_model], 'FP16');
      g.linear('w_master', 'x', [d_model, d_model]);
      g.cast('w_half', 'w_master', [d_model, d_model], 'FP16');
      g.matmul('y_half', 'x_half', 'w_half', [seq_len, d_model]);
      g.activation('act_half', 'y_half', 'SiLU');
      g.scale('loss_scale', 'act_half', 'Loss Scale');
      g.cast('out_fp32', 'loss_scale', [seq_len, d_model], 'FP32');
    }

    g.output('out', 'out_fp32');
    return g.build();
  }
};
